import type { Notification, NotificationState } from '../types/notification.types';

/**
 * N8 (86d3w2kez) part 3 — the toast routing matrix, as pure data.
 *
 * <b>Why the matrix is separate from the toaster.</b> "How long does a failure stay on screen, and
 * does it offer Retry?" is a product decision; `toast.error(...)` is a rendering call. Splitting them
 * means the decision is assertable in vitest — the ticket's own testing ruling — while the visual
 * timing and stacking stay by-eye, which is the only honest way to check them.
 */

/**
 * The four classes a posting outcome can fall into.
 *
 * ⚠️ `retryable` IS DEFINED-BUT-DORMANT IN v1, and that is deliberate rather than an oversight. The
 * v1 event feed carries QUEUED and TERMINAL frames only — the live "Retrying (2 of 3)" morph is
 * Phase 3 work (`OnProgressAsync`). Nothing on the SSE path produces this class today; only an
 * imperative click-ack does. It is declared now so the matrix is complete and so the next person does
 * not go hunting for the event that fires it. ⛔ Do not "fix" its absence.
 */
export type NotifyKind = 'success' | 'retryable' | 'terminal' | 'info';

export interface NotifyPlan {
  kind: NotifyKind;

  /**
   * How long the toast lives, in ms — or null for STICKY.
   *
   * A terminal failure sticks because it is the one outcome that needs a decision, and a decision the
   * user never saw is the same as no notification at all. Everything else expires.
   */
  duration: number | null;

  /**
   * ⛔ THE UPDATE-IN-PLACE KEY, and the reason a retry storm does not become a wall of toasts.
   *
   * Sonner replaces a toast when it is given an id it already holds. Keyed on the JOB, one posting
   * owns exactly one toast for its whole life — queued, retrying, settled — however many frames it
   * emits. Without it, a job that retries three times leaves three separate toasts on screen, two of
   * them describing states that have already passed.
   */
  id: string;

  /**
   * Buttons the toast offers, in order. Only a terminal failure earns one, and it is Open ALONE —
   * ⛔ deliberately NOT Retry. Open deep-links to the record, where the full recovery card renders
   * the reach disclosure ("do not re-post — reconcile"); a Retry on the toast would act without that
   * disclosure ever being seen, which is the one reading path that must not be skippable. The matrix
   * says exactly what the renderer does, so a passing test cannot invite anyone to "finish" a Retry
   * wiring that was never missing (2026-08-25 review).
   */
  actions: NotifyAction[];
}

export type NotifyAction = 'open';

const SUCCESS_MS = 5_000;
const RETRYABLE_MS = 10_000;

/** The matrix itself. One row per class, and nothing computes a duration anywhere else. */
const PLANS: Record<NotifyKind, Omit<NotifyPlan, 'id' | 'kind'>> = {
  success: { duration: SUCCESS_MS, actions: [] },
  retryable: { duration: RETRYABLE_MS, actions: [] },
  terminal: { duration: null, actions: ['open'] },
  info: { duration: SUCCESS_MS, actions: [] },
};

/**
 * The stable toast identity for a notification.
 *
 * Prefers the JOB, because one posting job is one story however many notification rows or frames
 * describe it. Falls back to the notification id for rows with no job behind them (comment,
 * procurement, system) so those still update in place rather than stacking.
 */
export function toastIdFor(n: Pick<Notification, 'id' | 'jobId'>): string {
  return n.jobId !== null ? `posting:${n.jobId}` : `notification:${n.id}`;
}

/**
 * Which class an outcome falls into — or null when it is not an outcome at all.
 *
 * ⛔ `queued` and `posting` return null ON PURPOSE. They are the START of a story, not its end, and
 * the user has already been told: they pressed the button, and the page said "Posting started". A
 * toast here tells them something they did two seconds ago, and it is the single easiest way to make
 * a notification centre feel like spam.
 */
export function kindForState(state: NotificationState): NotifyKind | null {
  switch (state) {
    case 'posted':
      return 'success';
    case 'failed':
    case 'reconcile':
      return 'terminal';
    case 'cancelled':
      return 'info';
    case 'retrying':
      return 'retryable';
    default:
      return null;
  }
}

/** The full plan for one notification, or null when it should not toast at all. */
export function planFor(n: Notification): NotifyPlan | null {
  const kind = kindForState(n.state);
  if (kind === null) return null;

  return { kind, id: toastIdFor(n), ...PLANS[kind] };
}

/**
 * The toast's words.
 *
 * ⛔ "Something went wrong" is BANNED by N8, and this is where that ban is enforced. Every line names
 * the document and, on a failure, the reason — because the point of the toast is to let someone decide
 * whether to stop what they are doing, and they cannot decide that from an apology.
 *
 * The copy is the SERVER's wherever the server has it: `title` and `errorSummary` come from
 * `NotificationCopyMap`, so the toast, the row and the page all say the same thing.
 */
export function messageFor(n: Notification): { title: string; description?: string } {
  const kind = kindForState(n.state);

  if (kind === 'terminal') {
    return {
      title: n.title,
      description: n.errorSummary ?? n.suggestion ?? undefined,
    };
  }

  return { title: n.title, description: n.body ?? undefined };
}
